import React, { useEffect, useMemo, useState } from 'react';
import { Box, DialogActions } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { formatDate, showError } from '@utils';
import { useFormErrorsNotification } from '@hooks';
import { customerVehiclesServices } from '@services';

import { ButtonV2 } from 'components/common/ui';
import { FieldV2 } from 'components/common/form-controls';

import validationSchemaEditVehicleInfoForm from './validationSchemaEditVehicleInfoForm';
import { CUSTOMER_VEHICLES_SIDEBAR_MAPPER } from '../constants';
import style from './editVehicleInfoForm.style';
import { FUEL_OPTIONS, TRANSMISSION_OPTIONS } from './constants';

const EditVehicleInfoForm = ({ defaultValues, onSubmit, onCancel }) => {
  const [isVinValid, setIsVinValid] = useState(false);

  const resolver = yupResolver(validationSchemaEditVehicleInfoForm);

  const methods = useForm({
    defaultValues,
    resolver,
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    trigger,
    reset,
    getValues,
  } = methods;

  const isDisabled = useMemo(() => {
    return !isVinValid || isSubmitting;
  }, [isVinValid, isSubmitting]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useFormErrorsNotification(methods.formState.errors);

  const handleVinBlur = async () => {
    try {
      const isValid = await trigger('vin');

      if (!isValid) {
        return;
      }

      const { vin } = getValues();

      if (vin) {
        const { data } = await customerVehiclesServices.validateByVin({
          vin,
        });
        if (data) {
          reset({
            vin,
            mileage: data.mileage,
            plate: data.plate,
            manufacturer: data.manufacturer,
            model: data.model,
            year: formatDate(`${data.year}`, 'yyyy'),
            transmission: data.transmission,
            edition: data.edition,
            displacement: data.displacement,
            fuel: data.fuel,
          });
        }
      }
      setIsVinValid(true);
    } catch (e) {
      showError(e);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={style.row}>
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.vin.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.vin.label}
            control={methods.control}
            disabled={isSubmitting}
            onBlur={handleVinBlur}
          />
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.type.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.type.label}
            control={methods.control}
            disabled
          />
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.mileage.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.mileage.label}
            control={methods.control}
            disabled={isDisabled}
          />
        </Box>
        <Box sx={style.row}>
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.plate.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.plate.label}
            control={methods.control}
            disabled={isDisabled}
          />
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.manufacturer.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.manufacturer.label}
            control={methods.control}
            disabled={isDisabled}
          />
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.model.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.model.label}
            control={methods.control}
            disabled={isDisabled}
          />
          <FieldV2
            type="number"
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.year.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.year.label}
            control={methods.control}
            disabled={isDisabled}
          />
        </Box>
        <Box sx={style.row}>
          <FieldV2
            type="dropdown"
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.transmission.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.transmission.label}
            control={methods.control}
            options={TRANSMISSION_OPTIONS}
            disabled={isDisabled}
          />
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.edition.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.edition.label}
            control={methods.control}
            disabled={isDisabled}
          />
          <FieldV2
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.displacement.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.displacement.label}
            control={methods.control}
            disabled={isDisabled}
          />
          <FieldV2
            type="dropdown"
            name={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.fuel.key}
            label={CUSTOMER_VEHICLES_SIDEBAR_MAPPER.fuel.label}
            control={methods.control}
            options={FUEL_OPTIONS}
            disabled={isDisabled}
          />
        </Box>
        <DialogActions>
          <ButtonV2
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonV2>
          <ButtonV2 type="submit" loading={isSubmitting} disabled={isDisabled}>
            OK
          </ButtonV2>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default EditVehicleInfoForm;
